import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, ExternalLink, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuthContext } from '../../../../../common/hooks/use-auth-context.hook';
import { ApiMethod } from '../../../../../common/models/api-types.type';
import { routes } from '../../../../../assets/constants';
import { config as vendorConfig } from '../../../../../common/hooks/use-settings-api.hook';
import { useResolveTranslation } from '../../../../../common/hooks/use-resolve-translation.hook';
import { FamilyStep } from '../setup/FamilyStep';
import { StepIndicator } from '../setup/StepIndicator';
import type { ProductFamily } from '../../../../../common/models/product-family.model';
import type { InitialProductData } from '../../hooks/useProductFormV3';
import type { SetupResult } from './CreateProductSetupDialog';
import Lottie from 'lottie-react';
import loadingAnimation from '../../../../../assets/loading-animation.json';

// =============================================================================
// Types
// =============================================================================

interface AiProductSetupFlowProps {
  families: ProductFamily[];
  familiesLoading: boolean;
  onComplete: (setup: SetupResult, scrapedData: InitialProductData) => void;
  onCancel: () => void;
}

/**
 * URL first, and nothing before it.
 *
 * The flow used to open by asking which family the product belonged to. That
 * question is answerable from the link itself — a page for a sofa and a page
 * for a floor tile do not look remotely alike — so asking it first made the
 * vendor tell us something they were about to show us anyway, and made the
 * fastest path through the feature three screens instead of one.
 *
 * The extractor now decides, and is only asked when the vendor sells more than
 * one family. When it cannot tell, the result screen asks — at which point
 * there is a product on screen to answer the question about.
 */
type AiStep = 'url' | 'loading' | 'result';

type StepId = 'url' | 'result';

// =============================================================================
// Helpers
// =============================================================================

function isValidUrl(str: string): boolean {
  try {
    const url = new URL(str);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

const STEP_LABEL_KEYS: Record<StepId, string> = {
  url: 'PAGES.MY_PRODUCTS.AI_SETUP.URL_LABEL',
  result: 'PAGES.MY_PRODUCTS.AI_SETUP.RESULT_TITLE',
};

/**
 * How long to wait after typing stops before reading the page.
 *
 * A pasted link is the normal case and fires at once. This is only for a URL
 * typed or edited by hand, where firing per keystroke would read half-written
 * addresses and bill for each one.
 */
const TYPING_SETTLE_MS = 900;

// Framer Motion variants for step transitions
const stepVariants = {
  enter: { opacity: 0, y: 16 },
  center: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] as const } },
  exit: { opacity: 0, y: -12, transition: { duration: 0.2, ease: [0.25, 0.1, 0.25, 1] as const } },
};

// =============================================================================
// Component
// =============================================================================

export const AiProductSetupFlow: React.FC<AiProductSetupFlowProps> = ({
  families,
  familiesLoading,
  onComplete,
  onCancel,
}) => {
  const { t } = useTranslation();
  const { rt } = useResolveTranslation();
  const { sendAuthGuardedRequest } = useAuthContext();

  // State
  const [step, setStep] = useState<AiStep>('url');
  const [selectedFamilyId, setSelectedFamilyId] = useState<string | null>(null);
  const [url, setUrl] = useState('');
  const [urlTouched, setUrlTouched] = useState(false);
  /** The last URL actually sent, so nothing pays to read the same page twice. */
  const scrapedUrl = useRef<string | null>(null);
  const [scrapeResult, setScrapeResult] = useState<any>(null);
  const [error, setError] = useState('');

  // One family means there was never a question to ask.
  useEffect(() => {
    if (families.length === 1) setSelectedFamilyId(families[0]._id);
  }, [families]);

  const selectedFamily = useMemo(() => {
    return families.find(f => f._id === selectedFamilyId) || null;
  }, [families, selectedFamilyId]);

  const urlValid = isValidUrl(url.trim());
  const showUrlError = urlTouched && url.trim().length > 0 && !urlValid;

  // Compute visible steps for the indicator
  const indicatorSteps: StepId[] = useMemo(() => ['url', 'result'], []);

  const currentIndicatorIndex = useMemo(() => {
    if (step === 'url') return indicatorSteps.indexOf('url');
    if (step === 'loading') return indicatorSteps.indexOf('url'); // loading is sub-step of url
    if (step === 'result') return indicatorSteps.indexOf('result');
    return 0;
  }, [step, indicatorSteps]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleFamilySelect = useCallback((familyId: string) => {
    setSelectedFamilyId(familyId);
  }, []);


  const handleScrape = useCallback(async (raw?: string) => {
    const target = (raw ?? url).trim();
    if (!isValidUrl(target)) return;

    scrapedUrl.current = target;
    setStep('loading');
    setError('');
    try {
      const response: any = await sendAuthGuardedRequest(
        ApiMethod.POST,
        routes.products.scrape,
        {
          url: target,
          language: vendorConfig?.language?.[0] || 'en',
          /*
           * What this vendor actually sells, so the extractor classifies into
           * a family they have rather than one they do not. Sent from here
           * because this is where the list already lives.
           */
          families: families.map((f) => ({ slug: f.slug, name: rt(f.name) })),
        },
      );
      setScrapeResult(response);

      /*
       * Trust the extractor's family only if it names one the vendor has.
       * Anything else leaves it unset and the result screen asks — a question
       * with the product on screen beside it, which is a far better moment to
       * ask than before the link was even pasted.
       */
      const matched = families.find((f) => f.slug === response?.familySlug);
      if (matched) setSelectedFamilyId(matched._id);

      setStep('result');
    } catch (err: any) {
      setError(err?.message || t('PAGES.MY_PRODUCTS.SCRAPE_ERROR'));
      setStep('url');
    }
  }, [url, families, rt, sendAuthGuardedRequest, t]);

  /**
   * Set when the extractor could not tell which family the page belongs to and
   * the vendor sells more than one. The result screen then asks, with the
   * product in front of them.
   */
  const needsFamily = !selectedFamilyId && families.length > 1;

  const handleCreate = useCallback(() => {
    if (!scrapeResult || !selectedFamilyId) return;

    const setup: SetupResult = {
      familyId: selectedFamilyId,
      applicationTypes: scrapeResult.applicationTypes,
      productType: scrapeResult.productType,
    };

    const initialData: InitialProductData = {
      name: scrapeResult.name,
      brand: scrapeResult.brand,
      description: scrapeResult.description,
      sku: scrapeResult.sku,
      productPageUrl: url.trim(),
      productType: scrapeResult.productType,
      applicationTypes: scrapeResult.applicationTypes,
      color: scrapeResult.color,
      size: scrapeResult.size,
      pattern: scrapeResult.pattern,
      // The photos it found. Shown on the result screen all along and then
      // dropped on the floor, which left the vendor an empty uploader under a
      // preview of exactly the images it should have contained.
      imageUrls: Array.isArray(scrapeResult.imageUrls) ? scrapeResult.imageUrls : undefined,
    };

    onComplete(setup, initialData);
  }, [scrapeResult, selectedFamilyId, url, onComplete]);

  const handleBack = useCallback(() => {
    if (step === 'result') {
      setStep('url');
      setScrapeResult(null);
      scrapedUrl.current = null;
    }
  }, [step]);

  /*
   * Reading starts on its own.
   *
   * A pasted link goes at once — pasting a product URL into a box labelled
   * "product URL" is not an ambiguous act, and an Analyze button after it is
   * a second click for a decision already made. A URL typed by hand waits for
   * the typing to settle, so half-written addresses are not read and billed.
   *
   * Guarded on the last URL actually sent, so a re-render, a stray keystroke
   * or coming back from the result screen cannot pay to read the same page
   * twice.
   */
  useEffect(() => {
    const target = url.trim();
    if (step !== 'url' || !isValidUrl(target) || scrapedUrl.current === target) return undefined;

    const id = window.setTimeout(() => void handleScrape(target), TYPING_SETTLE_MS);
    return () => window.clearTimeout(id);
  }, [url, step, handleScrape]);

  /** The result step is the only one with a decision left to press. */
  const isContinueEnabled = step === 'result' && !!scrapeResult && !!selectedFamilyId;

  const canGoBack = step === 'result';

  // Step title & description
  const getStepTitle = (): string => {
    if (step === 'url') return t('PAGES.MY_PRODUCTS.AI_SETUP.TITLE');
    if (step === 'loading') return t('PAGES.MY_PRODUCTS.SCRAPE_ANALYZING');
    if (step === 'result') return t('PAGES.MY_PRODUCTS.AI_SETUP.RESULT_TITLE');
    return '';
  };

  const getStepDescription = (): string => {
    if (step === 'url') return t('PAGES.MY_PRODUCTS.AI_SETUP.SUBTITLE');
    if (step === 'result') return t('PAGES.MY_PRODUCTS.AI_SETUP.RESULT_SUBTITLE');
    return '';
  };

  // Don't render during loading (it has its own full layout)
  if (step === 'loading') {
    return (
      <div className="flex min-h-[calc(100vh-140px)] flex-col items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        >
          {/* Sparkle icon with pulse */}
          <motion.div
            animate={{
              scale: [1, 1.1, 1],
              rotate: [0, 5, -5, 0],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            <div className="mb-6 flex size-[72px] items-center justify-center rounded-full bg-muted">
              <Sparkles className="size-9 text-muted-foreground" strokeWidth={1.5} />
            </div>
          </motion.div>

          {/* Lottie animation */}
          <Lottie
            animationData={loadingAnimation}
            style={{ width: 180, height: 180, marginTop: -16, marginBottom: -16 }}
          />

          {/* Text */}
          <p className="mb-2 font-heading text-base font-medium tracking-tight text-foreground">
            {t('PAGES.MY_PRODUCTS.SCRAPE_ANALYZING')}
          </p>

          {/* Animated dots */}
          <div className="mb-5 flex gap-1">
            {[0, 1, 2].map(i => (
              <motion.div
                key={i}
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{
                  duration: 1.4,
                  repeat: Infinity,
                  delay: i * 0.2,
                  ease: 'easeInOut',
                }}
              >
                <div className="size-1.5 rounded-full bg-muted-foreground" />
              </motion.div>
            ))}
          </div>

          {/* URL display */}
          <div className="flex max-w-[400px] items-center gap-1.5 border bg-muted px-4 py-1.5">
            <ExternalLink className="size-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate text-xs text-muted-foreground">{url}</span>
          </div>
        </motion.div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Main layout — matches CreateProductSetupDialog structure exactly
  // ---------------------------------------------------------------------------
  return (
    <div className="flex min-h-[calc(100vh-140px)] flex-col">
      {/* Back button row */}
      <div className="flex justify-start px-4 pt-2 sm:px-6">
        <Button
          variant="ghost"
          onClick={canGoBack ? handleBack : onCancel}
          className="h-9 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="rtl:rotate-180" />
          {t('COMMON.BACK')}
        </Button>
      </div>

      {/* Step indicator */}
      {indicatorSteps.length > 1 && (
        <div className="mx-auto w-full max-w-[640px] px-4 pb-3 sm:px-6">
          <StepIndicator
            steps={indicatorSteps}
            currentStepIndex={currentIndicatorIndex}
            labels={Object.fromEntries(
              Object.entries(STEP_LABEL_KEYS).map(([k, v]) => [k, t(v)])
            ) as Record<StepId, string>}
          />
        </div>
      )}

      {/* Main content — centered */}
      <div className="flex flex-1 flex-col items-center px-4 pb-8 sm:px-6">
        <div
          className={cn(
            'flex w-full flex-1 flex-col transition-all',
            step === 'result' ? 'max-w-[720px]' : 'max-w-[640px]',
          )}
        >
          {/* Step header */}
          <div className={cn('mb-8 text-center', indicatorSteps.length > 1 && 'mt-16')}>
            <div className="mb-2 flex items-center justify-center gap-3">
              {(step === 'url' || step === 'result') && (
                <Sparkles className="size-[22px] text-foreground" strokeWidth={1.5} />
              )}
              <h1 className="font-heading text-2xl font-medium tracking-tight text-foreground">
                {getStepTitle()}
              </h1>
              {step === 'url' && (
                <span className="self-center border px-1 py-px text-[9px] leading-snug font-medium tracking-wider text-muted-foreground uppercase">
                  Beta
                </span>
              )}
            </div>
            <p className="mx-auto max-w-[440px] text-sm leading-relaxed text-muted-foreground">
              {getStepDescription()}
            </p>
          </div>

          {/* Step content with animation */}
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              style={{ flex: 1 }}
            >
              {/* URL STEP */}
              {step === 'url' && (
                <div className="mx-auto w-full max-w-[480px]">
                  {/* URL input card */}
                  <div className="rounded-xl border bg-card p-6">
                    <label
                      htmlFor="ai-setup-url"
                      className="mb-2 block text-sm font-medium text-muted-foreground"
                    >
                      {t('PAGES.MY_PRODUCTS.AI_SETUP.URL_LABEL')}
                    </label>
                    <Input
                      id="ai-setup-url"
                      value={url}
                      onChange={(e) => {
                        setUrl(e.target.value);
                        if (!urlTouched) setUrlTouched(true);
                        if (error) setError('');
                      }}
                      onBlur={() => setUrlTouched(true)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && urlValid) void handleScrape();
                      }}
                      /*
                       * A paste is unambiguous, so it does not wait for the
                       * typing timer. Read from the event rather than from
                       * state, which has not been set yet at this point.
                       */
                      onPaste={(e) => {
                        const pasted = e.clipboardData.getData('text').trim();
                        if (isValidUrl(pasted)) {
                          setUrl(pasted);
                          setError('');
                          e.preventDefault();
                          void handleScrape(pasted);
                        }
                      }}
                      placeholder={t('PAGES.MY_PRODUCTS.SCRAPE_URL_PLACEHOLDER')}
                      aria-invalid={showUrlError || undefined}
                      className="h-10 text-sm"
                    />
                    {showUrlError && (
                      <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <p className="mt-1.5 text-xs text-destructive">
                          {t('PAGES.MY_PRODUCTS.AI_SETUP.INVALID_URL')}
                        </p>
                      </motion.div>
                    )}
                  </div>

                  {/* Error from scrape */}
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25 }}
                    >
                      <Alert variant="destructive" className="mt-4">
                        <AlertDescription className="flex flex-wrap items-center gap-3">
                          <span>{error}</span>
                          {/* Nothing else re-reads the same URL: the effect
                              above will not fire for a URL it already sent. */}
                          {urlValid && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => void handleScrape()}
                            >
                              {t('PAGES.MY_PRODUCTS.AI_SETUP.RETRY')}
                            </Button>
                          )}
                        </AlertDescription>
                      </Alert>
                    </motion.div>
                  )}
                </div>
              )}

              {/* RESULT STEP */}
              {step === 'result' && scrapeResult && (
                <div className="flex flex-col gap-6">
                  {/*
                    * The one question the page could not answer.
                    *
                    * Asked here rather than up front, and only when it is
                    * genuinely open: the vendor is looking at the product it
                    * is about, which makes "furniture or surfaces?" a glance
                    * instead of a guess.
                    */}
                  {needsFamily && (
                    <div>
                      <p className="mb-3 text-sm font-medium text-muted-foreground">
                        {t('PAGES.MY_PRODUCTS.AI_SETUP.WHICH_FAMILY')}
                      </p>
                      <FamilyStep
                        families={families}
                        selectedFamilyId={selectedFamilyId}
                        onSelect={handleFamilySelect}
                      />
                    </div>
                  )}

                  {/*
                    * What it decided, and how to disagree with it. A guess the
                    * vendor cannot see is a guess they find out about later,
                    * on a product filed where they will not look for it.
                    */}
                  {!needsFamily && selectedFamily && families.length > 1 && (
                    <div className="flex flex-wrap items-center justify-center gap-2">
                      <Badge variant="outline" className="gap-1">
                        {rt(selectedFamily.name)}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-muted-foreground"
                        onClick={() => setSelectedFamilyId(null)}
                      >
                        {t('PAGES.MY_PRODUCTS.AI_SETUP.CHANGE_FAMILY')}
                      </Button>
                    </div>
                  )}

                  <ResultPreview scrapeResult={scrapeResult} url={url} t={t} />
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/*
        * Only the result step has anything to press.
        *
        * The URL step reads the page on its own, so a button there would be
        * either dead or a second way to do what already happened. An empty
        * footer bar advertising neither is worse than no bar.
        */}
      {step === 'result' && (
        <div className="mt-auto flex justify-center border-t bg-card px-4 py-5 sm:px-6">
          <div className="flex w-full max-w-[720px] items-center justify-between">
            <div />

            <Button
              onClick={handleCreate}
              disabled={!isContinueEnabled}
              variant="outline"
              size="lg"
              className="px-7"
            >
              {t('PAGES.MY_PRODUCTS.SCRAPE_CREATE')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// Result Preview — redesigned summary
// =============================================================================

/** A titled panel; the result step is five of them stacked. */
const ResultSection: React.FC<{
  title: string;
  delay: number;
  children: React.ReactNode;
}> = ({ title, delay, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay }}
  >
    <div className="rounded-xl border bg-card p-6">
      <p className="mb-3 text-xs font-medium tracking-wider text-muted-foreground uppercase">
        {title}
      </p>
      {children}
    </div>
  </motion.div>
);

const ResultPreview: React.FC<{
  scrapeResult: any;
  url: string;
  t: (key: string) => string;
}> = ({ scrapeResult, url, t }) => {
  const hasImages = scrapeResult?.imageUrls?.length > 0;

  // Collect all data fields
  const fields: { label: string; value: React.ReactNode; span?: boolean }[] = [];

  if (scrapeResult?.brand) {
    fields.push({ label: t('PAGES.MY_PRODUCTS.SCRAPE_BRAND'), value: scrapeResult.brand });
  }
  if (scrapeResult?.sku) {
    fields.push({ label: 'SKU', value: scrapeResult.sku });
  }
  if (scrapeResult?.size) {
    fields.push({
      label: t('PAGES.MY_PRODUCTS.SCRAPE_SIZE'),
      value: `${scrapeResult.size.width} × ${scrapeResult.size.height} ${scrapeResult.size.unit || 'cm'}`,
    });
  }
  if (scrapeResult?.color) {
    fields.push({
      label: t('PAGES.MY_PRODUCTS.SCRAPE_COLOR'),
      value: (
        <span className="flex items-center gap-1.5">
          {scrapeResult.color.hex && (
            <span
              className="size-3.5 shrink-0 rounded-full border"
              style={{ backgroundColor: scrapeResult.color.hex }}
            />
          )}
          <span>{scrapeResult.color.value}</span>
        </span>
      ),
    });
  }
  if (scrapeResult?.material) {
    fields.push({ label: t('PAGES.MY_PRODUCTS.SCRAPE_MATERIAL'), value: scrapeResult.material });
  }
  if (scrapeResult?.pattern) {
    fields.push({ label: t('PAGES.MY_PRODUCTS.SCRAPE_PATTERN'), value: scrapeResult.pattern });
  }
  if (scrapeResult?.price) {
    fields.push({ label: t('PAGES.MY_PRODUCTS.SCRAPE_PRICE'), value: scrapeResult.price });
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Product name hero */}
      {scrapeResult?.name && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.05 }}
        >
          <div className="flex items-start gap-5 rounded-xl border bg-card p-6">
            {/* Lead image */}
            {hasImages && (
              <img
                src={scrapeResult.imageUrls[0]}
                alt="Product"
                className="size-20 shrink-0 border object-cover"
                onError={(e: any) => { e.target.style.display = 'none'; }}
              />
            )}
            <div className="min-w-0 flex-1">
              <p className="mb-1 font-heading text-base font-medium tracking-tight text-foreground">
                {scrapeResult.name}
              </p>
              {scrapeResult?.brand && (
                <p className="text-sm text-muted-foreground">{scrapeResult.brand}</p>
              )}
              {/* Source URL */}
              <div className="mt-2 flex items-center gap-1">
                <ExternalLink className="size-3 shrink-0 text-muted-foreground" />
                <span className="truncate text-xs text-muted-foreground">{url}</span>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Image gallery */}
      {hasImages && scrapeResult.imageUrls.length > 1 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <div className="flex gap-2 overflow-x-auto pb-1">
            {scrapeResult.imageUrls.slice(1, 8).map((imgUrl: string, i: number) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.25, delay: 0.12 + i * 0.05 }}
              >
                <img
                  src={imgUrl}
                  alt={`Product ${i + 2}`}
                  className="size-[72px] shrink-0 border object-cover transition-transform hover:scale-105 hover:border-foreground/30"
                  onError={(e: any) => { e.target.style.display = 'none'; }}
                />
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Data fields card */}
      {fields.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.15 }}
        >
          <div className="overflow-hidden rounded-xl border bg-card">
            {/* Section header */}
            <div className="border-b bg-muted px-6 py-3">
              <p className="text-xs font-medium tracking-wider text-muted-foreground uppercase">
                {t('PAGES.MY_PRODUCTS.AI_SETUP.PRODUCT_DETAILS')}
              </p>
            </div>

            {/* Fields as rows */}
            <div className="px-6 py-2">
              {fields.map((field, idx) => (
                <div
                  key={idx}
                  className={cn(
                    'flex items-center justify-between py-2.5',
                    idx < fields.length - 1 && 'border-b',
                  )}
                >
                  <span className="me-6 shrink-0 text-sm font-medium text-muted-foreground">
                    {field.label}
                  </span>
                  <span className="text-end text-sm font-medium text-foreground">
                    {field.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* Application types */}
      {scrapeResult?.applicationTypes?.length > 0 && (
        <ResultSection title={t('PAGES.MY_PRODUCTS.SCRAPE_APPLICATION')} delay={0.2}>
          <div className="flex flex-wrap gap-1.5">
            {scrapeResult.applicationTypes.map((type: string, i: number) => (
              <motion.div
                key={type}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2, delay: 0.22 + i * 0.04 }}
              >
                <Badge variant="outline">{type}</Badge>
              </motion.div>
            ))}
          </div>
        </ResultSection>
      )}

      {/* Description */}
      {scrapeResult?.description && (
        <ResultSection title={t('PAGES.MY_PRODUCTS.SCRAPE_DESCRIPTION')} delay={0.25}>
          <p className="max-h-20 overflow-hidden text-sm leading-relaxed text-foreground">
            {scrapeResult.description}
          </p>
        </ResultSection>
      )}
    </div>
  );
};

export default AiProductSetupFlow;
